import { c as _c } from "react/compiler-runtime";
import { getLabel } from '@/components/Molecules/DatePicker/DatePicker';
import { useEffectDeps } from '@/hooks/useEffectDeps';
import { useMounted } from '@/hooks/useMounted';
import { useSkipFirstCall } from '@/hooks/useSkipFirstCall';
import { trackEvent, trackSiteSearch } from '@/services/Analytics/BBAnalytics';
import type { SiteSearchCategory, TrackingCategory } from '@/services/Analytics/bnefAnalyticsSettings';
import { TrackingAction } from '@/services/Analytics/bnefAnalyticsSettings';
import { BrowserHttpClient } from '@/services/Http/BrowserHttpClient';
import type { HttpClient } from '@/services/Http/HttpClient';
import SessionStorage from '@/services/SessionStorage';
import { parseQueryAsDateRange, parseQueryAsSet, parseQueryAsSort } from '@/utils/queryParams';
import { safeJsonStringify } from '@/utils/strings';
import type { Filter, FilterSelection, Pagination, SearchDetails, Sort } from 'bnef';
import { useRouter } from 'next/router';
import { parseAsInteger, parseAsString, useQueryState, useQueryStates } from 'nuqs';
import type { ParsedUrlQuery } from 'querystring';
import type { ComponentProps, Context, PropsWithChildren } from 'react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
export const SEARCH_CONTEXT_STORAGE_KEY = 'search_context';

/**
 * A context that handles everything needed to provide a search page with:
 * - search box
 * - filters (differing by type: mutliselect/date/boolean)
 * - export buttons
 * - saved views
 * - results list
 * - pagination
 * - context query string
 * - update on change
 */
export interface ISearchContextValue<ResultType> {
  filters?: Filter[]; // the order matters as it'll be the order of display
  activeQuery: string;
  activeSort?: Sort;
  activeFilters?: Record<string, FilterSelection>;
  setActiveQuery: (param: string | null) => void;
  setActiveFilter: (param: Filter['param'], newValues: FilterSelection) => void;
  setActiveSort: (param: Sort | null) => void;
  pageSize?: number;
  setPageSize: (param: number | null) => void;
  /** The page currently being viewed. 1 when the URL doesn't say otherwise. */
  page: number;
  /** Moves to a page, refetching in-page when {@link clientSidePagination} is on. */
  setPage: (param: number | null) => void;
  /** Whether paging refetches in-page rather than navigating. */
  clientSidePagination: boolean;
  results?: SearchResults<ResultType>;
  isLoading: boolean;
  savedSearchDataset?: string;
  getCurrentUrlParams: () => Record<string, string>;
  getSearchDetails: () => SearchDetails;
  queryDefaults: Record<string, string>;
  reset: () => void;
}
export const getQueryDefaults = (filters: Filter[] = []): Record<string, string> => {
  const defaults: Record<string, string> = {};
  filters.forEach(filter => {
    if (filter.defaultOption) {
      defaults[filter.param] = filter.defaultOption;
    }
  });
  return defaults;
};
export interface ISearchContextProviderProps<ResultType> extends PropsWithChildren {
  performSearch: false | ((queryParams: ParsedUrlQuery, client: HttpClient) => Promise<SearchResults<ResultType>>);
  trackingCategory?: TrackingCategory;
  siteSearchCategory?: SiteSearchCategory;
  filters?: Filter[];
  initialResults?: SearchResults<ResultType>;
  savedSearchDataset?: string;
  defaultPageSize?: number;
  resetSortOnFilter?: boolean;
  /**
   * Per-filter hook that maps a filter's new value to a {@link Sort} to
   * apply automatically when that filter changes.
   *
   * Runs *after* {@link resetSortOnFilter}, so an override always wins over
   * the reset. Returning `null` clears the sort; returning `undefined`
   * leaves the current sort untouched.
   */
  filterSortOverrides?: Record<string, (value: FilterSelection) => Sort | null | undefined>;
  /**
   * Handles paging in-page, like every other control here, instead of letting
   * the pagination links navigate. The URL still carries the page, so it stays
   * linkable and the Back button still steps through the pages visited.
   *
   * Opt-in because the pagination bar is shared with lists that have no
   * in-page update path of their own and still expect to navigate.
   *
   * Anything else on the page that should follow the page number — a chart
   * showing only the current page, say — can read `page` off the context and
   * refetch alongside the list.
   */
  clientSidePagination?: boolean;
}
export type SearchResults<ResultType, Extras = any> = {
  data?: ResultType; // used if the search results aren't a list, but the entire dataset
  items: ResultType[];
  pagination?: Pagination;
  query?: Record<string, any>;
  facetCounts?: Record<string, Record<string, number>>;
  extras?: Extras;
};

/**
 * The page you are on when the URL says nothing about it, and the page anything
 * that changes the result set has to go back to.
 *
 * Written to the URL as its absence rather than as `page=1`, so the first page
 * of a search has the same address whether you arrived at it directly or paged
 * back to it.
 */
export const FIRST_PAGE = 1;
export const initialSearchContextState: ISearchContextValue<{}> = {
  activeQuery: '',
  setActiveFilter: () => {},
  setActiveSort: () => {},
  setActiveQuery: () => {},
  setPageSize: () => {},
  page: FIRST_PAGE,
  setPage: () => {},
  clientSidePagination: false,
  isLoading: false,
  getCurrentUrlParams: () => ({}),
  getSearchDetails: () => [],
  queryDefaults: {},
  reset: () => {}
};
export const SearchContext: Context<ISearchContextValue<any>> = createContext(initialSearchContextState);
export const filterMemoryCache: Record<string, any> = {};
const trackParams = (activeQuery: string, activeFilters: Record<string, FilterSelection>, trackingCategory?: ComponentProps<typeof SearchContextProvider>['trackingCategory'], siteSearchCategory?: ComponentProps<typeof SearchContextProvider>['siteSearchCategory']) => {
  if (activeQuery) {
    if (trackingCategory) {
      trackEvent(TrackingAction.Search, trackingCategory, activeQuery);
    }
    if (siteSearchCategory) {
      trackSiteSearch(activeQuery, siteSearchCategory);
    }
  }
  for (const key in activeFilters) {
    if (trackingCategory) {
      trackEvent(TrackingAction.Filter, trackingCategory, `${key}: ${safeJsonStringify(activeFilters[key])}`);
    }
  }
};
const KEY_QUERY = 'q';
const KEY_SORT = 'sort';
const KEY_PAGE_SIZE = 'pageSize';
const KEY_PAGE = 'page';
const useFilterQueryState = filters => {
  const $ = _c(10);
  let filterParams;
  if ($[0] !== filters) {
    filterParams = {};
    filters.forEach(filter => {
      [filter, filter.secondaryFilter].filter(Boolean).forEach(f => {
        if (f.type === "date") {
          f.options.forEach(opt => {
            filterParams[opt.value] = parseQueryAsDateRange;
          });
        } else {
          if (f.type === "year") {
            filterParams[f.param] = parseAsInteger;
          } else {
            if (f.type === "year-range") {
              filterParams[f.param] = parseQueryAsDateRange;
            } else {
              if (f.allowMultiple) {
                filterParams[f.param] = parseQueryAsSet;
              } else {
                filterParams[f.param] = parseAsString;
              }
            }
          }
        }
      });
    });
    $[0] = filters;
    $[1] = filterParams;
  } else {
    filterParams = $[1];
  }
  const [filterQueryStates, setFilterQueryStates] = useQueryStates(filterParams);
  let t0;
  let t1;
  if ($[2] !== setFilterQueryStates) {
    t0 = (param, values) => {
      setFilterQueryStates({
        [param]: values === undefined ? null : values
      });
    };
    t1 = () => {
      setFilterQueryStates(null);
    };
    $[2] = setFilterQueryStates;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  let t2;
  if ($[5] !== filterParams || $[6] !== filterQueryStates || $[7] !== t0 || $[8] !== t1) {
    t2 = {
      activeFilters: filterQueryStates,
      filterParams,
      setActiveFilter: t0,
      clearActiveFilters: t1
    };
    $[5] = filterParams;
    $[6] = filterQueryStates;
    $[7] = t0;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
export const SearchContextProvider = t0 => {
  const $ = _c(98);
  const {
    performSearch,
    trackingCategory,
    siteSearchCategory,
    initialResults,
    resetSortOnFilter: t1,
    filterSortOverrides,
    filters: t2,
    savedSearchDataset,
    defaultPageSize,
    clientSidePagination: t3,
    children
  } = t0;
  const resetSortOnFilter = t1 === undefined ? false : t1;
  let t4;
  if ($[0] !== t2) {
    t4 = t2 === undefined ? [] : t2;
    $[0] = t2;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const filters = t4;
  const clientSidePagination = t3 === undefined ? false : t3;
  const router = useRouter();
  const skipFirstRender = useSkipFirstCall();
  const isMounted = useMounted();
  const [resetCount, setResetCount] = useState(0);
  const [activeQuery, setActiveQuery] = useQueryState(KEY_QUERY, parseAsString);
  const [activeSort, setActiveSort] = useQueryState(KEY_SORT, parseQueryAsSort);
  let t5;
  if ($[2] !== defaultPageSize) {
    t5 = parseAsInteger.withDefault(defaultPageSize);
    $[2] = defaultPageSize;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const [pageSize, setPageSize] = useQueryState(KEY_PAGE_SIZE, t5);
  let t6;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = parseAsInteger.withDefault(FIRST_PAGE).withOptions({
      history: "push"
    });
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const [page, setPage] = useQueryState(KEY_PAGE, t6);
  const {
    activeFilters,
    filterParams,
    setActiveFilter,
    clearActiveFilters
  } = useFilterQueryState(filters);
  let t7;
  if ($[5] !== filters) {
    t7 = getQueryDefaults(filters);
    $[5] = filters;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const queryDefaults = t7;
  const [results, setResults] = useState(initialResults);
  const [isLoading, setLoading] = useState(false);
  const [abortController, setAbortController] = useState();
  const [isNavigating, setIsNavigating] = useState(false);
  let t8;
  if ($[7] !== clientSidePagination || $[8] !== setPage) {
    t8 = () => {
      if (clientSidePagination) {
        setPage(null);
      }
    };
    $[7] = clientSidePagination;
    $[8] = setPage;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const resetPage = t8;
  let t9;
  if ($[10] !== resetPage || $[11] !== setActiveQuery) {
    t9 = newQuery => {
      setActiveQuery(newQuery);
      resetPage();
    };
    $[10] = resetPage;
    $[11] = setActiveQuery;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const doSetActiveQuery = t9;
  let t10;
  if ($[13] !== resetPage || $[14] !== setActiveSort) {
    t10 = newSort => {
      setActiveSort(newSort);
      resetPage();
    };
    $[13] = resetPage;
    $[14] = setActiveSort;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  const doSetActiveSort = t10;
  let t11;
  if ($[16] !== resetPage || $[17] !== setPageSize) {
    t11 = newPageSize => {
      setPageSize(newPageSize);
      resetPage();
    };
    $[16] = resetPage;
    $[17] = setPageSize;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const doSetPageSize = t11;
  let t12;
  if ($[19] !== filterSortOverrides || $[20] !== resetPage || $[21] !== resetSortOnFilter || $[22] !== setActiveFilter || $[23] !== setActiveSort) {
    t12 = (param, newValues) => {
      setActiveFilter(param, newValues);
      if (resetSortOnFilter) {
        setActiveSort(null);
      }
      const override = filterSortOverrides?.[param];
      if (override) {
        const nextSort = override(newValues);
        if (nextSort !== undefined) {
          setActiveSort(nextSort);
        }
      }
      resetPage();
    };
    $[19] = filterSortOverrides;
    $[20] = resetPage;
    $[21] = resetSortOnFilter;
    $[22] = setActiveFilter;
    $[23] = setActiveSort;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  const doSetActiveFilter = t12;
  let t13;
  if ($[25] !== clearActiveFilters || $[26] !== resetPage || $[27] !== setActiveQuery || $[28] !== setActiveSort) {
    t13 = () => {
      setActiveQuery(null);
      setActiveSort(null);
      clearActiveFilters();
      resetPage();
      setResetCount(_temp);
    };
    $[25] = clearActiveFilters;
    $[26] = resetPage;
    $[27] = setActiveQuery;
    $[28] = setActiveSort;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  const reset = t13;
  let t14;
  if ($[30] !== activeFilters || $[31] !== activeQuery || $[32] !== activeSort || $[33] !== clientSidePagination || $[34] !== page || $[35] !== pageSize || $[36] !== queryDefaults) {
    t14 = () => {
      const queryParams = {
        ...queryDefaults
      } as any;
      if (activeQuery) {
        queryParams[KEY_QUERY] = activeQuery;
      }
      for (const key in activeFilters) {
        if (activeFilters[key] instanceof Set) {
          if ((activeFilters[key] as Set<string>).size > 0) {
            queryParams[key] = [...(activeFilters[key] as Set<string>)].sort();
          }
        } else {
          if (activeFilters[key]) {
            queryParams[key] = activeFilters[key];
          }
        }
      }
      if (activeSort) {
        queryParams[KEY_SORT] = parseQueryAsSort.serialize(activeSort);
      }
      if (pageSize) {
        queryParams[KEY_PAGE_SIZE] = pageSize;
      }
      if (clientSidePagination && page > FIRST_PAGE) {
        queryParams[KEY_PAGE] = page;
      }
      return queryParams;
    };
    $[30] = activeFilters;
    $[31] = activeQuery;
    $[32] = activeSort;
    $[33] = clientSidePagination;
    $[34] = page;
    $[35] = pageSize;
    $[36] = queryDefaults;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  const getParams = t14;
  let t15;
  if ($[38] !== activeFilters || $[39] !== activeQuery || $[40] !== activeSort || $[41] !== clientSidePagination || $[42] !== filterParams || $[43] !== page || $[44] !== pageSize) {
    t15 = () => {
      const queryParams_0 = {} as any;
      if (activeQuery) {
        const serialized = parseAsString.serialize(activeQuery);
        if (serialized !== null) {
          queryParams_0[KEY_QUERY] = serialized;
        }
      }
      for (const key_0 in activeFilters) {
        if (activeFilters[key_0] !== null && activeFilters[key_0] !== undefined) {
          const parser = filterParams[key_0];
          if (parser) {
            const serialized_0 = parser.serialize(activeFilters[key_0]);
            if (serialized_0 !== null) {
              queryParams_0[key_0] = serialized_0;
            }
          }
        }
      }
      if (activeSort) {
        const serialized_1 = parseQueryAsSort.serialize(activeSort);
        if (serialized_1 !== null) {
          queryParams_0[KEY_SORT] = serialized_1;
        }
      }
      if (pageSize) {
        const serialized_2 = parseAsInteger.serialize(pageSize);
        if (serialized_2 !== null) {
          queryParams_0[KEY_PAGE_SIZE] = serialized_2;
        }
      }
      if (clientSidePagination && page > FIRST_PAGE) {
        const serialized_3 = parseAsInteger.serialize(page);
        if (serialized_3 !== null) {
          queryParams_0[KEY_PAGE] = serialized_3;
        }
      }
      return queryParams_0;
    };
    $[38] = activeFilters;
    $[39] = activeQuery;
    $[40] = activeSort;
    $[41] = clientSidePagination;
    $[42] = filterParams;
    $[43] = page;
    $[44] = pageSize;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  const getCurrentUrlParams = t15;
  let t16;
  if ($[46] !== abortController || $[47] !== activeFilters || $[48] !== activeQuery || $[49] !== getCurrentUrlParams || $[50] !== getParams || $[51] !== isMounted || $[52] !== isNavigating || $[53] !== performSearch || $[54] !== router || $[55] !== siteSearchCategory || $[56] !== trackingCategory) {
    t16 = async () => {
      if (isNavigating) {
        return;
      }
      setLoading(true);
      trackParams(activeQuery, activeFilters, trackingCategory, siteSearchCategory);
      const queryParams_1 = getParams();
      if (performSearch === false) {
        setIsNavigating(true);
        await router.push({
          pathname: router.pathname,
          query: getCurrentUrlParams()
        }, undefined, {
          shallow: false
        });
        setLoading(false);
        return;
      }
      SessionStorage.set(SEARCH_CONTEXT_STORAGE_KEY, queryParams_1);
      abortController?.abort();
      const currentAbortController = new AbortController();
      setAbortController(currentAbortController);
      ;
      try {
        const results_0 = await performSearch(queryParams_1, new BrowserHttpClient(currentAbortController.signal));
        if (isMounted()) {
          setResults(results_0);
          setLoading(false);
        }
      } catch (t17) {
        const e = t17;
        if (!(e instanceof DOMException && e.name === "AbortError")) {
          throw e;
        }
      }
    };
    $[46] = abortController;
    $[47] = activeFilters;
    $[48] = activeQuery;
    $[49] = getCurrentUrlParams;
    $[50] = getParams;
    $[51] = isMounted;
    $[52] = isNavigating;
    $[53] = performSearch;
    $[54] = router;
    $[55] = siteSearchCategory;
    $[56] = trackingCategory;
    $[57] = t16;
  } else {
    t16 = $[57];
  }
  const doSearch = t16;
  let t17;
  if ($[58] !== getParams) {
    t17 = () => {
      const queryParams_2 = getParams();
      SessionStorage.set(SEARCH_CONTEXT_STORAGE_KEY, queryParams_2);
    };
    $[58] = getParams;
    $[59] = t17;
  } else {
    t17 = $[59];
  }
  let t18;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = [];
    $[60] = t18;
  } else {
    t18 = $[60];
  }
  useEffect(t17, t18);
  let t19;
  if ($[61] !== doSearch || $[62] !== skipFirstRender) {
    t19 = () => {
      skipFirstRender(doSearch);
    };
    $[61] = doSearch;
    $[62] = skipFirstRender;
    $[63] = t19;
  } else {
    t19 = $[63];
  }
  const t20 = clientSidePagination ? page : null;
  let t21;
  if ($[64] !== activeFilters || $[65] !== activeQuery || $[66] !== activeSort || $[67] !== pageSize || $[68] !== t20) {
    t21 = [activeQuery, activeSort, activeFilters, pageSize, t20];
    $[64] = activeFilters;
    $[65] = activeQuery;
    $[66] = activeSort;
    $[67] = pageSize;
    $[68] = t20;
    $[69] = t21;
  } else {
    t21 = $[69];
  }
  useEffectDeps(t19, t21);
  let t22;
  if ($[70] !== activeFilters || $[71] !== activeQuery || $[72] !== filters) {
    t22 = () => calculateSearchFilterDetails(activeFilters, activeQuery, filters);
    $[70] = activeFilters;
    $[71] = activeQuery;
    $[72] = filters;
    $[73] = t22;
  } else {
    t22 = $[73];
  }
  let t23;
  if ($[74] !== activeFilters || $[75] !== activeQuery || $[76] !== activeSort || $[77] !== clientSidePagination || $[78] !== doSetActiveFilter || $[79] !== doSetActiveQuery || $[80] !== doSetActiveSort || $[81] !== doSetPageSize || $[82] !== filters || $[83] !== getCurrentUrlParams || $[84] !== isLoading || $[85] !== page || $[86] !== pageSize || $[87] !== queryDefaults || $[88] !== reset || $[89] !== results || $[90] !== savedSearchDataset || $[91] !== setPage || $[92] !== t22) {
    t23 = {
      filters,
      activeQuery,
      setActiveQuery: doSetActiveQuery,
      activeFilters,
      setActiveFilter: doSetActiveFilter,
      activeSort,
      setActiveSort: doSetActiveSort,
      pageSize,
      setPageSize: doSetPageSize,
      page,
      setPage,
      clientSidePagination,
      results,
      isLoading,
      savedSearchDataset,
      getCurrentUrlParams,
      getSearchDetails: t22,
      queryDefaults,
      reset
    };
    $[74] = activeFilters;
    $[75] = activeQuery;
    $[76] = activeSort;
    $[77] = clientSidePagination;
    $[78] = doSetActiveFilter;
    $[79] = doSetActiveQuery;
    $[80] = doSetActiveSort;
    $[81] = doSetPageSize;
    $[82] = filters;
    $[83] = getCurrentUrlParams;
    $[84] = isLoading;
    $[85] = page;
    $[86] = pageSize;
    $[87] = queryDefaults;
    $[88] = reset;
    $[89] = results;
    $[90] = savedSearchDataset;
    $[91] = setPage;
    $[92] = t22;
    $[93] = t23;
  } else {
    t23 = $[93];
  }
  let t24;
  if ($[94] !== children || $[95] !== resetCount || $[96] !== t23) {
    t24 = <SearchContext.Provider key={resetCount} value={t23}>{children}</SearchContext.Provider>;
    $[94] = children;
    $[95] = resetCount;
    $[96] = t23;
    $[97] = t24;
  } else {
    t24 = $[97];
  }
  return t24;
};
const calculateSearchFilterDetails = (activeFilters: Record<string, FilterSelection>, activeQuery: string, filters: Filter[]): SearchDetails => {
  if (!filters) {
    return [];
  }
  const parts = [];
  if (activeQuery) {
    parts.push({
      value: [activeQuery]
    });
  }
  filters.forEach(filter => {
    [filter, filter.secondaryFilter].filter(Boolean).forEach(f => {
      if (f.type === 'organization') {
        if (activeFilters[f.param] instanceof Set && (activeFilters[f.param] as Set<string>).size > 0) {
          const values = [...(activeFilters[f.param] as Set<string>)].map(id => filterMemoryCache[f.param]?.[id]?.name).filter(Boolean);
          parts.push({
            label: f.label,
            value: values
          });
        }
        return;
      }
      if (f.type === 'date') {
        f.options.forEach(option => {
          if (activeFilters[option.value]) {
            parts.push({
              label: option.displayName,
              value: [getLabel(activeFilters[option.value], f.componentConfig.presets)]
            });
          }
        });
        return;
      }
      if (f.type === 'year') {
        if (activeFilters[f.param]) {
          parts.push({
            label: f.label,
            value: [activeFilters[f.param]]
          });
        }
        return;
      }
      if (f.type === 'year-range') {
        if (activeFilters[f.param]) {
          parts.push({
            label: f.label,
            value: [getLabel(activeFilters[f.param])]
          });
        }
        return;
      }
      if (activeFilters[f.param] instanceof Set && (activeFilters[f.param] as Set<string>).size > 0) {
        const values = Array.from(new Set(f.options.flatMap(option => option.options ?? option).filter(option => (activeFilters[f.param] as Set<string>).has(option.value)).map(option => option.displayName)));
        parts.push({
          label: f.label,
          value: values
        });
      }
      if (typeof activeFilters[f.param] === 'string' && activeFilters[f.param] !== '') {
        parts.push({
          label: f.label,
          value: [f.options.find(option => activeFilters[f.param] === option.value)?.displayName].filter(Boolean)
        });
      }
    });
  });
  return parts;
};
export function useSearchContext() {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error("useSearchContext must be used within an SearchContextProvider");
  }
  return context;
}
function _temp(prev) {
  return prev + 1;
}